import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Skeleton } from '../components/ui/skeleton';
import { toast } from 'sonner';
import {
  BookOpen,
  ArrowLeft,
  CheckCircle2,
  XCircle,
  Clock,
  Trophy,
  Home
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const StudentResults = () => {
  const { attemptId } = useParams();
  const navigate = useNavigate();
  
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('student_token');
    if (!token) {
      navigate('/student/login');
      return;
    }
    fetchResult(token);
  }, [attemptId, navigate]);

  const fetchResult = async (token) => {
    try {
      const response = await axios.get(`${API}/student/results/${attemptId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setResult(response.data);
    } catch (error) {
      console.error('Error fetching result:', error);
      toast.error('Failed to load result');
      navigate('/student/dashboard');
    } finally {
      setLoading(false);
    }
  };

  const getScoreColor = (percentage) => {
    if (percentage >= 90) return 'text-green-600';
    if (percentage >= 70) return 'text-primary';
    if (percentage >= 50) return 'text-yellow-600';
    return 'text-destructive';
  };

  const getScoreMessage = (percentage) => {
    if (percentage >= 90) return 'Excellent work!';
    if (percentage >= 70) return 'Good job!';
    if (percentage >= 50) return 'Keep practicing!';
    return 'Review the material and try again.';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <nav className="bg-white border-b border-border">
          <div className="max-w-4xl mx-auto px-4 py-4">
            <Skeleton className="h-8 w-40" />
          </div>
        </nav>
        <main className="max-w-4xl mx-auto px-4 py-8">
          <Skeleton className="h-48 w-full mb-8" />
          <Skeleton className="h-32 w-full mb-4" />
          <Skeleton className="h-32 w-full" />
        </main>
      </div>
    );
  }

  if (!result) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <h2 className="font-serif text-xl font-medium text-primary mb-2">Result not found</h2>
          <Button onClick={() => navigate('/student/dashboard')}>Back to Dashboard</Button>
        </div>
      </div>
    );
  }

  const correctCount = result.results.filter(r => r.is_correct).length;
  const pendingCount = result.results.filter(r => r.needs_review).length;

  return (
    <div className="min-h-screen bg-background">
      {/* Navbar */}
      <nav className="sticky top-0 z-50 bg-white/80 backdrop-blur-md border-b border-border">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => navigate('/student/dashboard')}
                data-testid="back-btn"
              >
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div>
                <h1 className="font-serif text-lg font-medium text-primary">Quiz Results</h1>
                <p className="text-xs text-muted-foreground">{result.quiz_title}</p>
              </div>
            </div>
            <Link to="/student/dashboard">
              <Button variant="outline" size="sm" className="gap-2">
                <Home className="w-4 h-4" />
                Dashboard
              </Button>
            </Link>
          </div>
        </div>
      </nav>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Score Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <Card className="border-border shadow-lg mb-8 overflow-hidden">
            <div className="bg-primary/5 p-8 text-center">
              <div className="w-20 h-20 bg-white rounded-full flex items-center justify-center mx-auto mb-4 shadow-md">
                <Trophy className={`w-10 h-10 ${getScoreColor(result.percentage)}`} />
              </div>
              <p className={`text-5xl font-serif font-bold ${getScoreColor(result.percentage)}`}>
                {Math.round(result.percentage)}%
              </p>
              <p className="text-lg text-muted-foreground mt-2">{getScoreMessage(result.percentage)}</p>
              
              {result.needs_review && (
                <div className="mt-4 inline-flex items-center gap-2 px-4 py-2 bg-accent/10 rounded-full text-accent text-sm">
                  <Clock className="w-4 h-4" />
                  {pendingCount} question{pendingCount > 1 ? 's' : ''} awaiting teacher review
                </div>
              )}
            </div>
            
            <CardContent className="p-6">
              <div className="grid grid-cols-3 gap-4 text-center">
                <div className="p-4 bg-secondary/50 rounded-xl">
                  <p className="text-2xl font-semibold text-primary">{correctCount}</p>
                  <p className="text-sm text-muted-foreground">Correct</p>
                </div>
                <div className="p-4 bg-secondary/50 rounded-xl">
                  <p className="text-2xl font-semibold text-destructive">
                    {result.results.length - correctCount - pendingCount}
                  </p>
                  <p className="text-sm text-muted-foreground">Incorrect</p>
                </div>
                <div className="p-4 bg-secondary/50 rounded-xl">
                  <p className="text-2xl font-semibold text-accent">{pendingCount}</p>
                  <p className="text-sm text-muted-foreground">Pending</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* Question Results */}
        <h2 className="font-serif text-xl font-medium text-primary mb-4">Question Breakdown</h2>
        <div className="space-y-4">
          {result.results.map((q, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.05 }}
            >
              <Card className={`border-2 ${
                q.needs_review 
                  ? 'border-accent/30 bg-accent/5' 
                  : q.is_correct 
                  ? 'border-green-200 bg-green-50' 
                  : 'border-red-200 bg-red-50'
              }`}>
                <CardContent className="p-4">
                  <div className="flex items-start gap-4">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                      q.needs_review 
                        ? 'bg-accent/20' 
                        : q.is_correct 
                        ? 'bg-green-100' 
                        : 'bg-red-100'
                    }`}>
                      {q.needs_review ? (
                        <Clock className="w-4 h-4 text-accent" />
                      ) : q.is_correct ? (
                        <CheckCircle2 className="w-4 h-4 text-green-600" />
                      ) : (
                        <XCircle className="w-4 h-4 text-red-600" />
                      )}
                    </div>
                    <div className="flex-1">
                      <p className="font-medium text-foreground mb-2">
                        <span className="text-muted-foreground mr-2">{index + 1}.</span>
                        {q.question}
                      </p>
                      
                      <div className="space-y-2 text-sm">
                        <p>
                          <span className="text-muted-foreground">Your answer: </span>
                          <span className={q.is_correct ? 'text-green-600 font-medium' : 'text-foreground'}>
                            {q.student_answer || '(No answer)'}
                          </span>
                        </p>
                        
                        {!q.needs_review && !q.is_correct && (
                          <p>
                            <span className="text-muted-foreground">Correct answer: </span>
                            <span className="text-primary font-medium">{q.correct_answer}</span>
                          </p>
                        )}
                        
                        {q.needs_review && (
                          <p className="text-accent italic">
                            This answer is being reviewed by your teacher
                          </p>
                        )}
                      </div>
                    </div>
                    <div className="text-right">
                      <span className={`text-lg font-semibold ${
                        q.needs_review ? 'text-accent' : q.is_correct ? 'text-green-600' : 'text-red-600'
                      }`}>
                        {q.needs_review ? '?' : q.points_earned}/{q.points_possible}
                      </span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </div>

        {/* Back to Dashboard */}
        <div className="mt-8 text-center">
          <Button
            onClick={() => navigate('/student/dashboard')}
            className="gap-2"
            data-testid="back-to-dashboard"
          >
            <Home className="w-4 h-4" />
            Back to Dashboard
          </Button>
        </div>
      </main>
    </div>
  );
};

export default StudentResults;
